// apps/web/src/routes/signs/AnnotationBox.tsx
import { useRef } from "react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { Trash2 } from "lucide-react";

export type AnnotationBoxData = {
  tempId: string; // client-side id for pre-save identity
  id?: number; // server id after save
  signLevel: number;
  pageIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
};

type Props = {
  box: AnnotationBoxData;
  pageHeightPx: number; // rendered height of the page in CSS pixels
  scale: number; // CSS-px per PDF-pt
  signerOptions: { level: number; label: string }[];
  onChange: (next: AnnotationBoxData) => void;
  onDelete: () => void;
};

/**
 * One draggable+resizable rectangle overlayed on a rendered PDF page.
 * Position math: the box.x/box.y are PDF user units (pt, bottom-left origin).
 * The rendered page uses CSS pixels with top-left origin, so we flip Y:
 *   cssTop = pageHeightPx - (y + h) * scale
 *   cssLeft = x * scale
 */
export function AnnotationBox({
  box,
  pageHeightPx,
  scale,
  signerOptions,
  onChange,
  onDelete,
}: Props) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const cssLeft = box.x * scale;
  const cssTop = pageHeightPx - (box.y + box.h) * scale;
  const cssW = box.w * scale;
  const cssH = box.h * scale;

  const handleDragStop = (_: unknown, data: { x: number; y: number }) => {
    // data.x, data.y are CSS-px positions of the box's top-left
    const newX = data.x / scale;
    const newY = (pageHeightPx - (data.y + cssH)) / scale;
    onChange({ ...box, x: newX, y: newY });
  };

  const handleResizeStop = (_: unknown, size: { size: { width: number; height: number } }) => {
    const newW = size.size.width / scale;
    const newH = size.size.height / scale;
    onChange({ ...box, w: newW, h: newH });
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      position={{ x: cssLeft, y: cssTop }}
      onStop={handleDragStop}
      bounds="parent"
      handle=".annot-handle"
    >
      {/*
        BẮT BUỘC có top:0/left:0. react-draggable định vị bằng transform
        translate(cssLeft, cssTop), mà transform lại tính từ VỊ TRÍ TĨNH của thẻ
        này. Thiếu top/left thì thẻ absolute lấy vị trí tĩnh là chỗ nó nằm trong
        dòng chảy bình thường — tức NGAY DƯỚI trang PDF vừa dựng — nên ô ký bị
        đẩy xuống dưới trang cả nghìn pixel, ngoài vùng nhìn thấy. Người dùng bấm
        "+ Thêm ô ký" thấy bộ đếm tăng nhưng không thấy ô đâu, tưởng chức năng hỏng.
      */}
      <div ref={nodeRef} style={{ position: "absolute", top: 0, left: 0, zIndex: 10 }}>
        <Resizable
          width={cssW}
          height={cssH}
          onResizeStop={handleResizeStop}
          minConstraints={[40, 20]}
          maxConstraints={[600, 400]}
        >
          <div
            style={{ width: cssW, height: cssH }}
            className="border-2 border-primary-600 bg-primary-500/15 group relative"
          >
            <div className="annot-handle absolute inset-x-0 top-0 h-5 bg-primary-600 text-white text-xs flex items-center justify-between px-1 cursor-move">
              <select
                className="bg-primary-600 text-white text-xs border-0 outline-none"
                value={box.signLevel}
                onChange={(e) => onChange({ ...box, signLevel: Number(e.target.value) })}
              >
                {signerOptions.map((s) => (
                  <option key={s.level} value={s.level}>
                    Cấp {s.level} — {s.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onDelete}
                className="hover:bg-primary-700 h-4 w-4 grid place-items-center rounded"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        </Resizable>
      </div>
    </Draggable>
  );
}
